import { Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from "@material-ui/core";
import React, { FC, ReactElement, useEffect, useState } from "react";
import { makeStyles } from '@material-ui/core/styles';
import { SourceConnection } from "../../models/SourceConnection";
import MetaMappingService from "../../services/MetaMappingService";
import { useHistory, useParams } from "react-router-dom";
import ClientSchemaService from "../../services/ClientSchemaService";
import RemoteSchemaService from "../../services/RemoteSchemaService";
import SourceConnectionService from "../../services/SourcesConnectionService";
import { ClientSchema } from "../../models/ClientSchema";
import { Alert } from "@material-ui/lab";
import moment from 'moment';
import { apiErrorMessage } from '../../utils/apiError';

const useStyles = makeStyles((theme) => ({
    button: {
        margin: theme.spacing(1),
        backgroundColor: '#349CC9',
        color: 'white',
    },
    root: {
        '& > *': {
            margin: theme.spacing(1),
            width: '25ch',
        },
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
        width: '100%',
    },
    fullWidthField: {
        width: '100%',
    },
}));

const SourcesMetaEditView: FC = (): ReactElement => {
    const { getClientSchemasForConnection } = ClientSchemaService();
    const { listSchemas } = RemoteSchemaService();
    const { getMetaMapping, saveMetaMapping } = MetaMappingService();
    const { getAllSourceConnections } = SourceConnectionService();

    const { id } = useParams<{ id?: string }>();
    const classes = useStyles();
    const history = useHistory();

    const [mappingName, setMappingName] = useState('');
    const [mappingPrefix, setMappingPrefix] = useState('');
    const [sources, setSources] = useState<SourceConnection[]>();
    const [selectedSourceConnectionId, setSelectedSourceConnectionId] = useState(0);
    const [selectedDestinationConnectionId, setSelectedDestinationConnectionId] = useState(0);
    const [selectedSchemaId, setSelectedSchemaId] = useState(0);
    const [selectedDestSchemaName, setSelectedDestSchemaName] = useState('');
    const [clientSchemas, setClientSchemas] = useState<ClientSchema[]>();
    const [destSchemaNames, setDestSchemaNames] = useState<string[]>([]);
    const [schemaSelectDisabled, setSchemaSelectDisabled] = useState(false);
    const [destSchemaLoading, setDestSchemaLoading] = useState(false);
    const [destSchemaError, setDestSchemaError] = useState<string>();
    const [saveError, setSaveError] = useState<string>();
    const [saving, setSaving] = useState(false);

    const getId = () => (id ? Number.parseInt(id, 10) : 0);

    useEffect(() => {
        if (id) {
            getMetaMapping(getId()).then((metaMapping) => {
                setMappingName(metaMapping.name);
                setMappingPrefix(metaMapping.prefix);
                setSelectedSourceConnectionId(metaMapping.sourceConnectionOrigin.id);
                setSelectedDestinationConnectionId(metaMapping.sourceConnectionDestination.id);
                if (metaMapping.clientSchema != null) {
                    setSelectedSchemaId(metaMapping.clientSchema.id);
                }
                if (metaMapping.destSchemaName?.trim()) {
                    setSelectedDestSchemaName(metaMapping.destSchemaName);
                }
            });
        }
        getAllSourceConnections().then((res) => setSources(res));
    }, []);

    useEffect(() => {
        if (selectedSourceConnectionId <= 0) return;
        getClientSchemasForConnection(selectedSourceConnectionId).then((schemas) => {
            setClientSchemas(schemas);
            setSchemaSelectDisabled(schemas.length === 0);
        });
    }, [selectedSourceConnectionId]);

    useEffect(() => {
        setDestSchemaError(undefined);
        if (selectedDestinationConnectionId <= 0) return;
        setDestSchemaLoading(true);
        listSchemas(selectedDestinationConnectionId)
            .then((res) => {
                if (!res.success && res.error?.length) {
                    setDestSchemaNames([]);
                    setDestSchemaError(res.error.join('; '));
                    return;
                }
                const names = res.data?.length ? res.data : [];
                setDestSchemaNames(names);
                if (!names.length) {
                    setDestSchemaError(
                        'No schemas returned. Test the Snowflake End Point and verify NIRVANA_SERVICE key-pair + role.'
                    );
                }
            })
            .catch((err) => {
                setDestSchemaNames([]);
                setDestSchemaError(apiErrorMessage(err));
            })
            .finally(() => setDestSchemaLoading(false));
    }, [selectedDestinationConnectionId]);

    return (
        <div>
            <Grid container spacing={3}>
                <Grid item xs={8}>
                    <form className={classes.root} noValidate autoComplete="off">
                        <TextField
                            className={classes.fullWidthField}
                            label="Meta Model Name"
                            variant="outlined"
                            value={mappingName}
                            onChange={(e) => setMappingName(e.target.value)}
                        />
                        <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel id="source-connection-id-label">Source Connection</InputLabel>
                            <Select
                                labelId="source-connection-id-label"
                                value={selectedSourceConnectionId}
                                onChange={(e) =>
                                    setSelectedSourceConnectionId(Number.parseInt(String(e.target.value), 10))
                                }
                                label="Source Connection"
                            >
                                {sources?.filter((s) => s.source.source).map((s) => (
                                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel id="source-schema-label">Source Schema</InputLabel>
                            <Select
                                labelId="source-schema-label"
                                value={selectedSchemaId}
                                onChange={(e) =>
                                    setSelectedSchemaId(Number.parseInt(String(e.target.value), 10))
                                }
                                label="Source Schema"
                                disabled={schemaSelectDisabled}
                            >
                                {clientSchemas?.map((cs) => (
                                    <MenuItem key={cs.id} value={cs.id}>
                                        {cs.schemaName}
                                        {cs.lastModified
                                            ? ` - ${moment(cs.lastModified).format('yyyy/MM/DD HH:mm:ss')}`
                                            : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel id="destination-connection-id-label">Destination Connection</InputLabel>
                            <Select
                                labelId="destination-connection-id-label"
                                value={selectedDestinationConnectionId}
                                onChange={(e) => {
                                    setDestSchemaNames([]);
                                    setSelectedDestSchemaName('');
                                    setSelectedDestinationConnectionId(
                                        Number.parseInt(String(e.target.value), 10)
                                    );
                                }}
                                label="Destination Connection"
                            >
                                {sources?.filter((s) => s.source.destination).map((s) => (
                                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {destSchemaError && !destSchemaLoading && (
                            <Alert severity="error" style={{ marginBottom: 8 }} onClose={() => setDestSchemaError(undefined)}>
                                {destSchemaError}
                            </Alert>
                        )}
                        <FormControl variant="outlined" className={classes.formControl}>
                            {destSchemaLoading && (
                                <Typography variant="body2" color="textSecondary">
                                    Getting destination schemas…
                                </Typography>
                            )}
                            {!destSchemaLoading && destSchemaNames.length > 0 && (
                                <>
                                    <InputLabel id="dest-schema-name-label">Destination Schema</InputLabel>
                                    <Select
                                        labelId="dest-schema-name-label"
                                        value={selectedDestSchemaName}
                                        onChange={(e) => setSelectedDestSchemaName(String(e.target.value))}
                                        label="Destination Schema"
                                    >
                                        {destSchemaNames.map((s) => (
                                            <MenuItem key={s} value={s}>{s}</MenuItem>
                                        ))}
                                    </Select>
                                </>
                            )}
                        </FormControl>
                        <TextField
                            className={classes.fullWidthField}
                            style={{ width: 200 }}
                            label="Tables Prefix"
                            variant="outlined"
                            value={mappingPrefix}
                            onChange={(e) => setMappingPrefix(e.target.value)}
                        />
                        <Typography variant="caption" display="block">
                            Prefix to apply to tables on migration. For example NR_ turns Accounts into NR_Accounts.
                        </Typography>
                    </form>
                </Grid>
            </Grid>
            {saveError && (
                <Alert severity="error" style={{ marginTop: 8 }} onClose={() => setSaveError(undefined)}>
                    {saveError}
                </Alert>
            )}
            <Button
                variant="contained"
                className={classes.button}
                disabled={saving}
                onClick={() => {
                    setSaving(true);
                    setSaveError(undefined);
                    saveMetaMapping({
                        metaMappingId: getId(),
                        name: mappingName,
                        prefix: mappingPrefix,
                        sourceConnectionDestinationId: selectedDestinationConnectionId,
                        sourceConnectionOriginId: selectedSourceConnectionId,
                        clientSchemaId: selectedSchemaId,
                        destSchemaName: selectedDestSchemaName,
                    })
                        .then((res) => {
                            if (id) {
                                history.push('/sources-meta');
                            } else {
                                history.push(`/sources-meta/setup/${res}`);
                            }
                        })
                        .catch((err) => setSaveError(apiErrorMessage(err)))
                        .finally(() => setSaving(false));
                }}
            >
                {saving ? 'Saving…' : 'Save'}
            </Button>
        </div>
    );
};

export default SourcesMetaEditView;
